//src/components/gro/SurveyResultsSummary.tsx
import { BarChart3, Clock3, Users, CheckCircle2, AlertTriangle } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type {
  SurveyCampaignDimensionResult,
  SurveyCampaignResponseSummary,
} from "@/types/gro";

type Props = {
  summary: SurveyCampaignResponseSummary;
  dimensions: SurveyCampaignDimensionResult[];
};

function getOverallAverage(dimensions: SurveyCampaignDimensionResult[]) {
  if (dimensions.length === 0) return 0;
  const sum = dimensions.reduce((acc, d) => acc + d.average_score, 0);
  return +(sum / dimensions.length).toFixed(2);
}

function getRiskColor(rate: number) {
  if (rate >= 75) return "text-primary";
  if (rate >= 50) return "text-accent-foreground";
  return "text-destructive";
}

export function SurveyResultsSummary({ summary, dimensions }: Props) {
  const overallAvg = getOverallAverage(dimensions);
  const responseRate = summary.response_rate;
  const progressValue = Math.min(100, Math.max(0, responseRate));

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {/* Participantes */}
      <Card className="relative overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Participantes</p>
              <p className="text-2xl font-bold tracking-tight">{summary.total_participants}</p>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Público total da campanha</p>
        </CardContent>
      </Card>

      {/* Respostas */}
      <Card className="relative overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <CheckCircle2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Respostas</p>
              <p className="text-2xl font-bold tracking-tight">{summary.total_responses}</p>
            </div>
          </div>
          <div className="mt-3 space-y-1">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Taxa de resposta</span>
              <span className={`font-semibold ${getRiskColor(responseRate)}`}>
                {responseRate}%
              </span>
            </div>
            <Progress value={progressValue} className="h-1.5" />
          </div>
        </CardContent>
      </Card>

      {/* Pendentes */}
      <Card className="relative overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent">
              <Clock3 className="h-5 w-5 text-accent-foreground" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Pendentes</p>
              <p className="text-2xl font-bold tracking-tight">{summary.pending_tokens}</p>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Ainda sem resposta</p>
        </CardContent>
      </Card>

      {/* Dimensões */}
      <Card className="relative overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Dimensões</p>
              <p className="text-2xl font-bold tracking-tight">{dimensions.length}</p>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Dimensões avaliadas</p>
        </CardContent>
      </Card>

      {/* Leitura geral */}
      <Card className="relative overflow-hidden border-primary/20 bg-primary/5">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15">
              <AlertTriangle className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Média geral</p>
              <p className="text-2xl font-bold tracking-tight text-primary">{overallAvg}</p>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Média das {dimensions.length} dimensões
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
